import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Col, Container, Row, Card, CardBody, Badge, Modal, ModalHeader, ModalBody, Button,
} from 'reactstrap';
import CloseIcon from 'mdi-react/CloseIcon';
import SettingsIcon from 'mdi-react/SettingsIcon';
import PropTypes from 'prop-types';
import oauthService from '../../../services/oauthService';
import { SiteName } from '../../../shared/components/domainSupport';
import LoadingSpinner from '../../../shared/components/widgets/LoadingSpinner';
import { jsonHeader, isProdEnv } from '../../../config';
import { isDarkTheme } from '../../../shared/helpers/themeTableStyles';
import OnboardingSettingsPanel from '../../../shared/components/onboardingSettings/OnboardingSettingsPanel';
import { getOnboardingSettingsConfig } from '../../../shared/components/onboardingSettings/onboardingSettingsConfig';

/**
 * UnifiedOAuthCallback - Handles OAuth callback from ad platforms
 *
 * NOTE: "adPlatform" prop refers to AD PLATFORM (amazon, walmart, google, tiktok, etc.)
 * This is different from SYSTEM PLATFORM (limitless, adjuggernaut, adrev)
 *
 * @param {string} adPlatform - The ad platform that is completing OAuth (e.g., 'google', 'tiktok', 'amazon')
 * @param {string} apiType - The API type being authorized (e.g., 'ad-api', 'account-api')
 */
const UnifiedOAuthCallback = ({ adPlatform, apiType = 'ad-api' }) => {
  const user = useSelector(state => state.authentication.user);
  const sellerSelect = useSelector(state => state.sellerSelect);
  const theme = useSelector(state => state.theme);
  const darkMode = isDarkTheme(theme);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Controls the "Confirm Settings" modal overlay. We can't know ahead of
  // time whether this account's settings are already complete -- that's
  // only knowable once OnboardingSettingsPanel's discover call resolves,
  // and it only runs once mounted inside the (open) modal. So we always
  // auto-open the modal as soon as OAuth succeeds; handleDiscovered below
  // just tracks whether everything was already filled in, purely to tweak
  // the "Complete Setup" vs "Review Confirmation Settings" button wording
  // for next time (it does NOT auto-close the modal on its own).
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(null);

  useEffect(() => {
    const completeOAuthFlow = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        // DEV-ONLY TEST MODE: append ?skipOAuth=1&accountId=<id> to this page's URL to
        // bypass the code/state exchange with the OAuth provider entirely and jump
        // straight to the "success" state below. Useful for testing the Confirm
        // Settings modal without running a real OAuth consent flow -- just make sure
        // you're logged in (real JWT) and have already saved the platform's access
        // token yourself (e.g. "Meta Business Access Token") via Account Settings for
        // that accountId first, since the discover/confirm calls still hit the real
        // backend and expect it to exist. accountId is required here because this page
        // lives under the public onboarding layout, which has no seller selected in
        // Redux to fall back on. Never available in production.
        const testAccountId = urlParams.get('accountId');
        if (!isProdEnv && urlParams.get('skipOAuth') === '1' && testAccountId) {
          setResult({
            status: 'success',
            accountId: testAccountId,
            message: `[TEST MODE] Skipped OAuth code exchange -- assuming ${adPlatform} access token is already saved for account ${testAccountId}.`,
          });
          setSuccess(true);
          setLoading(false);
          return;
        }

        // Extract OAuth parameters
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Check for errors from OAuth provider
        if (errorParam) {
          setError(errorDescription || errorParam);
          setLoading(false);
          return;
        }

        // Check required parameters
        if (!code || !state) {
          setError('Missing required OAuth parameters (code or state)');
          setLoading(false);
          return;
        }

        // Extract platform-specific parameters
        const additionalParams = {};
        urlParams.forEach((value, key) => {
          if (!['code', 'state', 'error', 'error_description'].includes(key)) {
            additionalParams[key] = value;
          }
        });

        // Complete OAuth flow
        const headerWithAuth = {
          ...jsonHeader,
          authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
        };

        const data = await oauthService.completeOAuth({
          platform: adPlatform,
          apiType,
          code,
          state,
          additionalParams,
          options: {
            method: 'POST',
            headers: headerWithAuth,
          },
        });

        setResult(data);
        setSuccess(true);
      } catch (err) {
        console.error('OAuth completion error:', err);
        setError(err.message || 'Failed to complete OAuth authorization');
      } finally {
        setLoading(false);
      }
    };

    completeOAuthFlow();
  }, [adPlatform, apiType, user]);

  const platformName = adPlatform.charAt(0).toUpperCase() + adPlatform.slice(1);
  const supportText = SiteName() === 'Limitless' ? (
    <h4>For onboarding support, please contact <a href="mailto:adteam@limitless.tech">adteam@limitless.tech</a></h4>
  ) : null;

  // Self-service settings picker (discover/confirm) is only shown for
  // platforms that have a config registered (currently: Meta). Adding a new
  // platform is just a matter of adding an entry to onboardingSettingsConfig.
  const settingsConfig = getOnboardingSettingsConfig(adPlatform);

  // accountId can come back from the completeOAuth response (either at the
  // top level or nested under metadata), or fall back to the logged-in
  // user's currently selected account.
  const accountId = result?.accountId
    ?? result?.metadata?.accountId
    ?? sellerSelect?.value
    ?? null;

  // Prefer the logged-in user's JWT. If the user is not logged in (public
  // onboarding link flow), use the short-lived onboardingToken returned by
  // the backend so discover/confirm calls can still be authorized.
  const settingsToken = user?.jwtToken || result?.onboardingToken || null;

  const toggleSettings = () => setIsSettingsOpen(prev => !prev);

  // Called once the discover call returns (the modal is already open by
  // then -- see the effect below). Tracks whether EVERY field this
  // platform tracks (see onboardingSettingsConfig.js) is already populated,
  // purely so the trigger button can say "Review Confirmation Settings"
  // instead of "Complete Setup" the next time this page loads.
  const handleDiscovered = (discovered) => {
    const fieldKeys = settingsConfig?.fields?.map(field => field.key) || [];
    const allComplete = fieldKeys.length > 0
      && fieldKeys.every(key => !!(discovered || {})[key]);
    setHasExistingSettings(allComplete);
  };

  // Once the user confirms/saves their settings, close the overlay -- they
  // can still reopen it via the "Review Confirmation Settings" button.
  const handleConfirmSuccess = () => {
    setIsSettingsOpen(false);
    setHasExistingSettings(true);
  };

  // Everything was already discovered fully populated (a re-onboarding
  // user with nothing changed) -- nothing to save, so just close the
  // overlay when they click "Keep Previous Settings".
  const handleKeepExisting = () => {
    setIsSettingsOpen(false);
    setHasExistingSettings(true);
  };

  const showSettingsFeature = !!(settingsConfig && accountId);

  // Auto-pop the Confirm Settings modal open as soon as OAuth succeeds and
  // we know the settings feature applies to this platform/account. We
  // can't wait to see if settings are already complete before deciding to
  // open it -- that's only known once discover (which runs inside the
  // modal, via OnboardingSettingsPanel) resolves -- so we always open on
  // arrival and let the user close it themselves once they're done.
  useEffect(() => {
    if (showSettingsFeature) {
      setIsSettingsOpen(true);
    }
  }, [showSettingsFeature]);

  return (
    <Container className="dashboard">
      <Row>
        <Col md={12}>
          <h3 className="page-title">
            {platformName} OAuth - API Onboarding
          </h3>
        </Col>
      </Row>
      <Row><br /></Row>
      <Row>
        <Col md={8} className="mx-auto">
          <div className="account__wrapper">
            <LoadingSpinner active={loading} />

            {!loading && error && (
              <>
                <h4 className="text-danger">Authorization Error</h4>
                <p>There was an error during the OAuth authorization process:</p>
                <br />
                <Card>
                  <CardBody>
                    <h5 className="bold-text text-danger">Error Details:</h5>
                    <p>{error}</p>
                  </CardBody>
                </Card>
                <br />
                {supportText}
              </>
            )}

            {!loading && !error && success && (
              <>
                <Card className="border-success">
                  <CardBody>
                    <div className="card__title">
                      <h5 className="bold-text d-flex align-items-center">
                        <Badge color="success" style={{ marginRight: '12px' }}>✓ Connected</Badge>
                        <span>{platformName} Authorization Successful</span>
                      </h5>
                    </div>
                    <p className="mb-0">Your {platformName} account has been successfully authorized and attached.</p>
                    {result?.metadata?.accountName && <p className="mb-0"><strong>Account:</strong> {result.metadata.accountName}</p>}
                    {result?.message && <p className="mb-0">{result.message}</p>}

                    {showSettingsFeature && (
                      <>
                        <hr />
                        <p className="mb-2">
                          <strong>Did you complete the last onboarding step?</strong> Your {platformName} configuration
                          {' '}(Business, Ad Account, Page, Instagram Account, Pixel and Catalog) only needs to be
                          saved once. If not, review and save it below. If you already have, feel free to skip this
                          -- but you&apos;re welcome to double-check it below.
                        </p>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={toggleSettings}
                        >
                          <SettingsIcon size={16} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
                          {hasExistingSettings ? 'View Confirmation Settings' : 'Review & Save Settings'}
                        </Button>
                      </>
                    )}
                  </CardBody>
                </Card>

                <br />
                {supportText}
                <br />
                <h4>Thank You!</h4>
              </>
            )}
          </div>
        </Col>
      </Row>

      {showSettingsFeature && (
        <Modal
          isOpen={isSettingsOpen}
          toggle={toggleSettings}
          size="xl"
          className={theme.className}
          style={{ maxWidth: '95vw' }}
        >
          <ModalHeader tag="div" close={<span />}>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="close"
                onClick={toggleSettings}
                aria-label="Close"
                style={{ marginRight: '12px' }}
              >
                <CloseIcon size={20} style={{ color: darkMode ? '#dddddd' : '#1c1c1c' }} />
              </button>
              <span>{platformName} Account Settings</span>
            </div>
          </ModalHeader>
          <ModalBody style={{ minHeight: '60vh' }}>
            <OnboardingSettingsPanel
              platform={adPlatform}
              accountId={accountId}
              token={settingsToken}
              fieldsConfig={settingsConfig}
              platformDisplayName={platformName}
              onDiscovered={handleDiscovered}
              onConfirmSuccess={handleConfirmSuccess}
              onKeepExisting={handleKeepExisting}
            />
          </ModalBody>
        </Modal>
      )}
    </Container>
  );
};

UnifiedOAuthCallback.propTypes = {
  adPlatform: PropTypes.string.isRequired,
  apiType: PropTypes.string,
};

UnifiedOAuthCallback.defaultProps = {
  apiType: 'ad-api',
};

export default UnifiedOAuthCallback;
